
import Vue from 'vue';
import CalibrationList from '@/components/dashboard/calibration/CalibrationList.vue';
import { DeviceCalibration, DeviceCalibrationStatuses } from '@/interfaces/calibration';
import { Zone } from '@/interfaces/lighting';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';
import { AxiosError } from 'axios';
import permissions from '@/lib/permissions';

interface ComponentData {
	poller: number | null;
	selectedId: number | null;
}

export default Vue.extend({
	components: {
		ConfirmActionModal,
		CalibrationList,
	},

	data(): ComponentData {
		return {
			poller: null,
			selectedId: null,
		};
	},

	computed: {
		userCanReadCalibrations(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'groups',
				'read',
			]);
		},
		userCanWriteCalibrations(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'groups',
				'write',
			]);
		},
		activeLocale(): string {
			return this.$i18n.locale;
		},
		calibrations(): DeviceCalibration[] {
			return this.$store.getters['devices/calibrations/getCalibrations'];
		},
		zones(): Zone[] {
			return this.$store.getters['locations/zones/getZones'];
		},
	},

	methods: {
		changeZoneCalibration(
			dateTime: string,
			daylightMode: boolean,
			lightLevel: number,
			zoneId: number,
			status: DeviceCalibrationStatuses
		): void {
			this.selectedId = zoneId;
			if (['Scheduled', 'Started'].includes(status)) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.confirmationModal as any).show();
			} else {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				this.onCalibrationStart(dateTime, daylightMode, zoneId, lightLevel);
			}
		},

		// daylightMode is for future. Waiting for API endpoint for it
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		async onCalibrationStart(
			dateTime: string,
			daylightMode: boolean,
			zoneId: number,
			lightLevel: number
		): Promise<void> {
			if (zoneId && dateTime && this.userCanWriteCalibrations) {
				this.$store.dispatch('tools/loading/changeLoadingStatus');

				try {
					await this.$store.dispatch('devices/calibrations/modifyCalibration', {
						id_group: zoneId,
						payload: {
							status: 'Scheduled',
							dt_calibration: dateTime,
							dt_start: null,
							dt_end: null,
							outdoor: daylightMode,
							light_level: lightLevel,
							stage: 0,
						},
					});
					this.selectedId = null;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-start-calibration',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
				this.$store.dispatch('tools/loading/changeLoadingStatus');
			}
		},

		async onConfirmCalibartionCancellation(): Promise<void> {
			if (this.selectedId && this.userCanWriteCalibrations) {
				this.$store.dispatch('tools/loading/changeLoadingStatus');
				try {
					await this.$store.dispatch('devices/calibrations/modifyCalibration', {
						id_group: this.selectedId,
						payload: { status: 'Canceled', dt_calibration: null, dt_start: null, dt_end: null },
					});
					this.selectedId = null;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					(this.$refs.confirmationModal as any).hide();
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-cancel-calibration',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
				this.$store.dispatch('tools/loading/changeLoadingStatus');
			}
		},

		onCancelCalibrationCancellation(): void {
			this.selectedId = null;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmationModal as any).hide();
		},
	},

	mounted() {
		this.poller = window.setInterval(
			() => this.$store.dispatch('devices/calibrations/fetchCalibrations'),
			60000
		);
	},

	destroyed() {
		if (this.poller) {
			window.clearInterval(this.poller);
			this.poller = null;
		}
	},
});
